/**
 * EX-6 — shared negation-polarity detector for BOOLEAN claims.
 *
 * Lifted out of the site-context channel (`extractClaims.ts`, where it
 * was named `detectNegationPolarity` and applied only to site-context
 * boolean claims) into a shared, dependency-light module so BOTH the
 * site-context channel AND the doc channel (`extractFromDocument.ts`)
 * can apply the SAME conservative polarity flip.
 *
 * The LLM frequently emits the wrong polarity on negated facts:
 *   - "workstreams are NOT yet initiated" → the model returns `false`,
 *     but the claim "not-initiated" is TRUE.
 *   - "the site is NOT in an SFHA" → the model returns `true` for
 *     `flood.sfha` because it saw "SFHA", when the doc says the opposite.
 *
 * Contract: returns `true` when the text expresses NEGATION of the
 * subject predicate, and `null` when the text gives no clear polarity
 * signal (so the caller should keep the LLM's value). It NEVER returns
 * `false` — absence of a negation marker is "no signal", not
 * "affirmative", so a missing marker leaves the LLM value untouched.
 *
 * Strategy (intentionally conservative): scan for an unambiguous
 * negation marker ("no", "not", "not yet", "n't", "never", "without",
 * …). We do NOT attempt full NLP — we only flip when a negation marker
 * is clearly present, so a false-flip is far less likely than the
 * current always-trust-the-LLM bug.
 *
 * Pure / synchronous. Pure data + no IO (CLAUDE.md §MODELLING).
 */
export function detectNegationPolarity(text: string): boolean | null {
  if (!text) return null;
  const t = ` ${text.toLowerCase().replace(/[’']/g, "'")} `;
  // Strong negation markers (word-boundary). Ordered most-specific first.
  const negationRe =
    /\b(no|not|not\s+yet|never|without|none|cannot|can't|won't|isn't|aren't|wasn't|weren't|hasn't|haven't|hadn't|doesn't|don't|didn't|unfiled|not\s+been|has\s+not|have\s+not|had\s+not|yet\s+to)\b/;
  const hasNegation = negationRe.test(t);
  if (hasNegation) return true;
  return null;
}
